import React, { useEffect, useState } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useActions, useAppSelector } from 'hooks';
import { accessTokenSelect, authThunk, isLoadingSelect } from 'state';
import { routes } from 'enums';
import { readStoredTokens } from 'config';

/**
 * Wraps the logged-out pages: login, reset-password, change-password.
 *
 * The `getMe()` below used to be unconditional. With no session that is a
 * guaranteed 401, and the axios response interceptor treats a 401 on any
 * non-auth path as an expired session: it attempts a refresh, finds no refresh
 * token, and calls forceLogout(), which hard-navigates to /login.
 *
 * On /login that was invisible, because forceLogout skips when already there.
 * On /reset-password and /change-password it threw an anonymous visitor back
 * to /login within a second of arriving -- so the forgot-password flow could
 * not be started, let alone finished, by the only person who ever needs it: an
 * admin who is locked out. Probing it anonymously showed two GET /auth/me
 * calls and a redirect, every time.
 *
 * A stored token is what distinguishes "restoring a session" from "never
 * logged in", which is exactly how PrivateLayout already decides it.
 */
export const PublicLayout = () => {
  const location = useLocation();
  const { getMe } = useActions(authThunk);
  const token = useAppSelector(accessTokenSelect);
  const isLoading = useAppSelector(isLoadingSelect);
  const [hasStoredToken] = useState(() => !!readStoredTokens().accessToken);

  useEffect(() => {
    if (hasStoredToken) {
      getMe();
    }
  }, []);

  // Only meaningful while a real session is being restored. With no stored
  // token there is nothing to wait for, and `isLoading` starts true, so
  // blocking on it unconditionally would render a blank page over the login
  // form until something else happened to settle it.
  if (hasStoredToken && isLoading) return <></>;

  return token ? (
    <Navigate to={routes.MAIN.DASHBOARD} replace state={location} />
  ) : (
    <Outlet />
  );
};
